import { ConfirmDialog } from "@/components/common/ConfirmDialog/ConfirmDialog"
import { toast } from "@/components/common/Toast"
import { SharepointConnectButton } from "@/components/feature/SharepointConnect/SharepointConnectButton"
import { SharepointFolderPicker } from "@/components/feature/SharepointConnect/SharepointFolderPicker"
import { SharepointStatusIndicator } from "@/components/feature/SharepointConnect/SharepointStatusIndicator"
import { SHAREPOINT_CONFIG } from "@/config/sharepoint"
import { useSharepointBrowse } from "@/hooks/useSharepointBrowse"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useAddFolderSelections,
  useCreateSharepointConnection,
  useDeleteAllSharepointFiles,
  useDeleteSharepointConnection,
  useGetSharepointConnection,
  useGetSyncStatus,
  useTriggerAutoSync,
  useTriggerSync,
} from "@/services/api/sharepointConnectionService"
import type { KnowledgebaseFile } from "@/types/knowledgebase"
import type { SharepointFolderSelection } from "@/types/sharepointConnection"
import { isLimitError } from "@/utils/limitErrors"
import { getSharepointFilesCount } from "@/utils/sharepointUtils"
import { IconCloudOff, IconTrash } from "@tabler/icons-react"
import { useCallback, useEffect, useMemo, useState } from "react"

interface KnowledgebaseSharepointSectionProps {
  filesData: KnowledgebaseFile[] | undefined
  refetchFiles: () => void
  isDatabaseTourActive: boolean
  compact?: boolean
  connectHandlerRef?: React.MutableRefObject<(() => void) | undefined>
}

export const KnowledgebaseSharepointSection: React.FC<
  KnowledgebaseSharepointSectionProps
> = ({
  filesData,
  refetchFiles,
  isDatabaseTourActive,
  compact = false,
  connectHandlerRef,
}) => {
  const { translate } = useTranslation()

  const [showFolderPicker, setShowFolderPicker] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [isUnlinkAllModalOpen, setIsUnlinkAllModalOpen] = useState(false)
  const [isUnlinkingAll, setIsUnlinkingAll] = useState(false)

  const sharepointBrowse = useSharepointBrowse()
  const { data: sharepointConnection, isLoading: isConnectionLoading } =
    useGetSharepointConnection()
  const { data: syncStatus } = useGetSyncStatus(
    sharepointConnection?.id,
    sharepointConnection?.isSyncing || false,
  )
  const { mutate: deleteConnection } = useDeleteSharepointConnection()
  const { mutateAsync: createConnectionAsync } = useCreateSharepointConnection()
  const { mutateAsync: addFolderSelectionsAsync } = useAddFolderSelections()
  const { mutateAsync: triggerSyncAsync } = useTriggerSync()
  const { mutateAsync: triggerAutoSyncAsync } = useTriggerAutoSync()
  const { mutate: deleteAllSharepointFiles } = useDeleteAllSharepointFiles()

  const sharepointFilesCount = useMemo(() => {
    return getSharepointFilesCount(filesData ?? [])
  }, [filesData])

  // Auto-sync SharePoint on page load
  useEffect(() => {
    const triggerAutoSync = async () => {
      if (!sharepointConnection || sharepointConnection.isSyncing) return

      const hasSyncedFolders = sharepointConnection.folderSelections.some(
        (s) => s.lastSyncedAt !== null,
      )
      if (!hasSyncedFolders) return

      const cooldownKey = `sharepoint-auto-sync-${sharepointConnection.id}`
      const lastSync = localStorage.getItem(cooldownKey)
      if (
        lastSync &&
        Date.now() - Number(lastSync) < SHAREPOINT_CONFIG.AUTO_SYNC_COOLDOWN
      )
        return

      try {
        const token = await sharepointBrowse.getAccessToken()
        await triggerAutoSyncAsync({
          connectionId: sharepointConnection.id,
          data: { microsoftAccessToken: token },
        })
        localStorage.setItem(cooldownKey, String(Date.now()))
      } catch (error) {
        console.error("Auto-sync failed:", error)
      }
    }

    triggerAutoSync()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharepointConnection?.id])

  const handleSharepointConnect = useCallback(async () => {
    try {
      setConnectError(null)

      const response = await sharepointBrowse.login()
      const account = response.account
      const displayName = account.name || account.username || ""
      const email = account.username || ""
      const tenantId = account.tenantId || ""

      const accessToken = await sharepointBrowse.getAccessToken()

      await createConnectionAsync({
        microsoftAccessToken: accessToken,
        displayName,
        email,
        tenantId,
        folders: [],
      })

      setShowFolderPicker(true)
    } catch (err) {
      console.error("Failed to connect:", err)
      setConnectError(translate("SHAREPOINT/CONNECT_ERROR"))
    }
  }, [sharepointBrowse, createConnectionAsync])

  useEffect(() => {
    if (connectHandlerRef) {
      connectHandlerRef.current = handleSharepointConnect
    }
  }, [connectHandlerRef, handleSharepointConnect])

  const handleStartSync = useCallback(
    async (
      accessToken: string,
      folders: SharepointFolderSelection[],
    ): Promise<string> => {
      if (!sharepointConnection) {
        throw new Error("No SharePoint connection found")
      }

      const newSelections = await addFolderSelectionsAsync({
        connectionId: sharepointConnection.id,
        folders,
        microsoftAccessToken: accessToken,
      })

      const selectionIds = newSelections.map((s) => s.id)
      await triggerSyncAsync({
        connectionId: sharepointConnection.id,
        data: {
          microsoftAccessToken: accessToken,
          selectionIds,
        },
      })

      return sharepointConnection.id
    },
    [addFolderSelectionsAsync, triggerSyncAsync, sharepointConnection],
  )

  const handleDisconnect = useCallback(() => {
    setIsDisconnectModalOpen(true)
  }, [])

  const confirmDisconnect = useCallback(() => {
    setIsDisconnecting(true)
    const doLogout = () => {
      sharepointBrowse.logout()
      setShowFolderPicker(false)
      setIsDisconnecting(false)
      setIsDisconnectModalOpen(false)
      toast.success(translate("SHAREPOINT/DISCONNECTED_TOAST"))
      refetchFiles()
    }

    if (sharepointConnection) {
      deleteConnection(sharepointConnection.id, {
        onSuccess: doLogout,
        onError: (error) => {
          if (!isLimitError(error)) {
            toast.error(translate("SHAREPOINT/DISCONNECT_ERROR_TOAST"))
          }
          setIsDisconnecting(false)
          setIsDisconnectModalOpen(false)
        },
      })
    } else {
      doLogout()
    }
  }, [
    sharepointConnection,
    deleteConnection,
    sharepointBrowse,
    refetchFiles,
    translate,
  ])

  const handleManageClick = useCallback(() => {
    setShowFolderPicker(true)
  }, [])

  const handleUnlinkAll = useCallback(() => {
    setIsUnlinkAllModalOpen(true)
  }, [])

  const confirmUnlinkAll = useCallback(() => {
    setIsUnlinkingAll(true)
    deleteAllSharepointFiles(undefined, {
      onSuccess: (data) => {
        if (data.failed > 0 && data.deleted > 0) {
          toast.warning(
            translate("SHAREPOINT/UNLINK_PARTIAL_TOAST", {
              deleted: data.deleted.toString(),
              failed: data.failed.toString(),
            }),
          )
        } else if (data.failed > 0) {
          toast.error(translate("SHAREPOINT/UNLINK_ERROR_TOAST"))
        } else {
          toast.success(
            translate("SHAREPOINT/UNLINK_SUCCESS_TOAST", {
              count: data.deleted.toString(),
            }),
          )
        }
        setIsUnlinkingAll(false)
        setIsUnlinkAllModalOpen(false)
        refetchFiles()
      },
      onError: (error) => {
        if (!isLimitError(error)) {
          toast.error(translate("SHAREPOINT/UNLINK_ERROR_TOAST"))
        }
        setIsUnlinkingAll(false)
        setIsUnlinkAllModalOpen(false)
      },
    })
  }, [deleteAllSharepointFiles, refetchFiles, translate])

  return (
    <>
      {/* Status indicator in the upload panel */}
      {!isDatabaseTourActive && (
        <div className={compact ? undefined : "mt-4"}>
          {sharepointConnection ? (
            <SharepointStatusIndicator
              connection={sharepointConnection}
              syncingCount={
                syncStatus?.status === "syncing" ? syncStatus.filesTotal : 0
              }
              importedFilesCount={sharepointFilesCount}
              onManageClick={handleManageClick}
              compact={compact}
            />
          ) : (
            <SharepointConnectButton
              onConnect={handleSharepointConnect}
              loading={isConnectionLoading || sharepointBrowse.loading}
              error={connectError}
              compact={compact}
            />
          )}
        </div>
      )}

      {/* Folder Picker Modal */}
      {showFolderPicker && (
        <SharepointFolderPicker
          onClose={() => setShowFolderPicker(false)}
          onStartSync={handleStartSync}
          onSyncComplete={() => refetchFiles()}
          onDisconnect={sharepointConnection ? handleDisconnect : undefined}
          onUnlinkAll={handleUnlinkAll}
          importedFiles={filesData}
          connectionId={sharepointConnection?.id}
          sharepointBrowse={sharepointBrowse}
        />
      )}

      {/* Confirm dialogs */}
      {isUnlinkAllModalOpen && (
        <ConfirmDialog
          title={translate("SHAREPOINT/UNLINK_ALL_TITLE")}
          message={translate("SHAREPOINT/UNLINK_ALL_MESSAGE")}
          confirmLabel={translate("SHAREPOINT/UNLINK_ALL_BUTTON")}
          cancelLabel={translate("SHAREPOINT/CANCEL_BUTTON")}
          onConfirm={confirmUnlinkAll}
          onCancel={() => setIsUnlinkAllModalOpen(false)}
          icon={IconTrash}
          variant="danger"
          isLoading={isUnlinkingAll}
        />
      )}
      {isDisconnectModalOpen && (
        <ConfirmDialog
          title={translate("SHAREPOINT/DISCONNECT_TITLE")}
          message={translate("SHAREPOINT/DISCONNECT_MESSAGE")}
          confirmLabel={translate("SHAREPOINT/DISCONNECT_BUTTON")}
          cancelLabel={translate("SHAREPOINT/CANCEL_BUTTON")}
          onConfirm={confirmDisconnect}
          onCancel={() => setIsDisconnectModalOpen(false)}
          icon={IconCloudOff}
          variant="danger"
          isLoading={isDisconnecting}
        />
      )}
    </>
  )
}
